/**
 * Login page with dark theme styling.
 */
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';

export function LoginPage() {
  const { isAuthenticated, isLoading, error, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: Location })?.from?.pathname || '/';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleSignIn = async () => {
    await signIn();
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-dark-950">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-dark-950 px-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-primary-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-accent-500/10 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo/Brand */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 shadow-lg shadow-primary-500/25">
            <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gradient">Dynatron-AI</h1>
          <p className="mt-2 text-dark-400">Portfolio Management Dashboard</p>
        </div>

        {/* Login Card */}
        <div className="rounded-2xl border border-dark-700/50 bg-dark-800/80 backdrop-blur-xl p-8 shadow-xl">
          <h2 className="mb-6 text-xl font-semibold text-dark-100">Sign In</h2>

          {error && (
            <div
              className="mb-4 rounded-lg bg-rose-500/10 border border-rose-500/20 p-4 text-sm text-rose-400"
              role="alert"
            >
              {error}
            </div>
          )}

          <Button
            variant="gradient"
            size="lg"
            onClick={handleSignIn}
            className="w-full"
            glow
          >
            Sign in with Cognito
          </Button>

          <p className="mt-4 text-center text-sm text-dark-500">
            You will be redirected to the secure login page
          </p>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-dark-600">
          Secure authentication powered by AWS Cognito
        </p>
      </div>
    </div>
  );
}
